import { hookToAttributeSelector } from '@wix/panda-js-utils';
import { INITIAL_DESKTOP_PADDING as INITIAL_USE_VIEWBOX_DESKTOP_PADDING } from '../hooks';
import { DH } from '../data-hooks';
import { round } from './utils';
import { elementsToShapes } from './elements-to-shapes';
import { rotatePoint } from './geometry';
const DEFAULT_INITIAL_PLAN_PADDING = 32;
export const getViewBoxString = ({ scale, container, x: initialX, y: initialY }) => {
    const { x, y, width, height } = getScaledPannedViewBox({ scale, container, x: initialX, y: initialY });
    return `${x} ${y} ${width} ${height}`;
};
export const getScaledPannedViewBox = ({ scale, container, x: initialX, y: initialY }) => {
    const { centeringX, centeringY, width, height } = getScaledViewBox({ scale, container });
    const x = initialX - centeringX;
    const y = initialY - centeringY;
    return { x, y, width, height };
};
export const getScaledViewBox = ({ scale, container, customCentering }) => {
    const { clientWidth, clientHeight } = container;
    const width = clientWidth / scale;
    const height = clientHeight / scale;
    const deltaWidth = width - clientWidth;
    const deltaHeight = height - clientHeight;
    const centeringX = deltaWidth / 2;
    const centeringY = deltaHeight / 2;
    let deltaX = 0;
    let deltaY = 0;
    if (customCentering) {
        const { oldScale, zoomPosition } = customCentering;
        const oldMidX = clientWidth / oldScale / 2;
        const oldMidY = clientHeight / oldScale / 2;
        const diffFromMidX = zoomPosition.x / oldScale - oldMidX;
        const diffFromMidY = zoomPosition.y / oldScale - oldMidY;
        deltaX += diffFromMidX;
        deltaY += diffFromMidY;
        const newDiffFromMidX = (diffFromMidX * oldScale) / scale;
        const newDiffFromMidY = (diffFromMidY * oldScale) / scale;
        deltaX -= newDiffFromMidX;
        deltaY -= newDiffFromMidY;
    }
    return {
        centeringX,
        centeringY,
        width,
        height,
        deltaX,
        deltaY,
    };
};
export const getFactualElementPosition = ({ element, definition = null, container, svgViewBox, returnBoundingBox = false, useMeasuringRectangle = false, }) => {
    const actionContainerPosition = container.getBoundingClientRect();
    const { centeringX, centeringY } = getScaledViewBox({
        scale: svgViewBox.scale,
        container,
    });
    const measuringRect = useMeasuringRectangle
        ? element.querySelector(hookToAttributeSelector(DH.SHAPE_MEASURING_RECT)) ?? element
        : element;
    const { x: boundingX, y: boundingY, width: boundingWidth, height: boundingHeight, } = measuringRect.getBoundingClientRect();
    const scaledBoundingWidth = boundingWidth / svgViewBox.scale;
    const scaledBoundingHeight = boundingHeight / svgViewBox.scale;
    const midX = (boundingX - actionContainerPosition.x) / svgViewBox.scale +
        round(scaledBoundingWidth / 2) -
        centeringX +
        svgViewBox.x;
    const midY = (boundingY - actionContainerPosition.y) / svgViewBox.scale +
        round(scaledBoundingHeight / 2) -
        centeringY +
        svgViewBox.y;
    if (returnBoundingBox) {
        const scaledBoundingX = midX - round(scaledBoundingWidth / 2);
        const scaledBoundingY = midY - round(scaledBoundingHeight / 2);
        return {
            x: scaledBoundingX,
            y: scaledBoundingY,
            width: scaledBoundingWidth,
            height: scaledBoundingHeight,
        };
    }
    const x = midX - round(definition.width / 2);
    const y = midY - round(definition.height / 2);
    return { x, y, width: definition.width, height: definition.height };
};
export const getOuterElementBoundingRectangle = ({ x, y, width, height, angle = 0 }) => {
    const center = { x: x + width / 2, y: y + height / 2 };
    const corners = [
        { x, y },
        { x: x + width, y },
        { x, y: y + height },
        { x: x + width, y: y + height },
    ];
    const rotatedCorners = corners.map(point => rotatePoint(point, center, angle));
    const minX = Math.min(...rotatedCorners.map(p => p.x));
    const maxX = Math.max(...rotatedCorners.map(p => p.x));
    const minY = Math.min(...rotatedCorners.map(p => p.y));
    const maxY = Math.max(...rotatedCorners.map(p => p.y));
    const newWidth = maxX - minX;
    const newHeight = maxY - minY;
    const newX = x + (width - newWidth) / 2;
    const newY = y + (height - newHeight) / 2;
    return {
        x: newX,
        y: newY,
        width: maxX - minX,
        height: maxY - minY,
    };
};
export const getPlanBoundingRectangle = (plan) => {
    const shapes = elementsToShapes({ plan, shouldUseCategories: false });
    let box = null;
    for (const shape of shapes) {
        const { x, y, width, height } = getOuterElementBoundingRectangle(shape);
        if (box === null) {
            box = { width, height, x, y };
            continue;
        }
        let maxX = box.x + box.width;
        let maxY = box.y + box.height;
        box.x = box.x > x ? x : box.x;
        box.y = box.y > y ? y : box.y;
        const shapeEndX = x + width;
        const shapeEndY = y + height;
        maxX = maxX < shapeEndX ? shapeEndX : maxX;
        maxY = maxY < shapeEndY ? shapeEndY : maxY;
        box.width = maxX - box.x;
        box.height = maxY - box.y;
    }
    return box;
};
export const getPlanViewBoxValues = ({ plan, container, padding = INITIAL_USE_VIEWBOX_DESKTOP_PADDING, forcedContainerDimensions, }) => {
    const planBox = getPlanBoundingRectangle(plan);
    let containerBox;
    if (forcedContainerDimensions) {
        containerBox = forcedContainerDimensions;
    }
    else if (container) {
        containerBox = container.getBoundingClientRect();
    }
    else {
        containerBox = planBox;
    }
    if (!planBox) {
        return { x: 0, y: 0, width: containerBox.width, height: containerBox.height };
    }
    const planRatio = planBox.width / planBox.height;
    const containerRatio = containerBox.width / containerBox.height;
    if (planRatio >= containerRatio) {
        const width = planBox.width;
        const height = (width * containerBox.height) / containerBox.width;
        const x = planBox.x;
        const y = -(height / 2 - planBox.height / 2) + planBox.y;
        const resizeRatio = containerBox.width / width;
        return addBoundingBoxPadding({ width, height, x, y }, true, padding / resizeRatio);
    }
    else {
        const height = planBox.height;
        const width = (height * containerBox.width) / containerBox.height;
        const y = planBox.y;
        const x = -(width / 2 - planBox.width / 2) + planBox.x;
        const resizeRatio = containerBox.height / height;
        return addBoundingBoxPadding({ width, height, x, y }, false, padding / resizeRatio);
    }
};
export const addBoundingBoxPadding = (box, widthBasedSizing, padding) => {
    if (widthBasedSizing) {
        const adjustedPadding = (padding * box.width) / (box.width - 2 * padding);
        const x = box.x - adjustedPadding;
        const width = box.width + adjustedPadding * 2;
        const height = (width * box.height) / box.width;
        const y = box.y - (height - box.height) / 2;
        return { x, y, width, height };
    }
    else {
        const adjustedPadding = (padding * box.height) / (box.height - 2 * padding);
        const y = box.y - adjustedPadding;
        const height = box.height + adjustedPadding * 2;
        const width = (height * box.width) / box.height;
        const x = box.x - (width - box.width) / 2;
        return { x, y, width, height };
    }
};
export const getInitialViewBox = ({ plan, container, padding = DEFAULT_INITIAL_PLAN_PADDING, forcedContainerDimensions, }) => {
    const { x, y, width, height } = forcedContainerDimensions
        ? getPlanViewBoxValues({ plan, forcedContainerDimensions, padding })
        : getPlanViewBoxValues({ plan, container, padding });
    return `${x} ${y} ${width} ${height}`;
};
//# sourceMappingURL=bounding-boxes.js.map